import type { Rect } from "./geometry";
import type { GraphicText } from "./graphic-tree";

const PANEL_ADVANCES: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(
    [
      "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      "0.482 0.470 0.464 0.476 0.438 0.421 0.467 0.480 0.230 0.452 0.491 0.426 0.548 0.514 0.473 0.465 0.461 0.471 0.444 0.468 0.479 0.488 0.689 0.475 0.474 0.412",
      "abcdefghijklmnopqrstuvwxyz",
      "0.446 0.445 0.434 0.445 0.436 0.298 0.441 0.447 0.219 0.217 0.449 0.209 0.675 0.447 0.442 0.448 0.448 0.324 0.412 0.291 0.447 0.436 0.613 0.439 0.423 0.373",
      "0123456789",
      "0.453 0.284 0.438 0.436 0.484 0.439 0.440 0.407 0.440 0.435",
      " .,:;!?-/+()[]",
      "0.200 0.223 0.211 0.275 0.232 0.279 0.437 0.331 0.415 0.438 0.311 0.311 0.345 0.345",
    ].flatMap((value, index, values) => {
      if (index % 2 !== 0) return [];
      const characters = value;
      const advances = values[index + 1]!.split(" ").map(Number);
      return [...characters].map((character, characterIndex) => [
        character,
        advances[characterIndex]!,
      ]);
    }),
  ),
);

type VerticalGlyphMetric = readonly [minimum: number, maximum: number];

// These are the visible (atlas padding removed) vertical extents shared by the
// four bundled Barlow Condensed MSDF weights, normalized to the 96 px source
// size. Pixi positions bitmap glyphs after its baseline offset, so their paint
// does not begin at y=0 even when the text's vertical alignment is "top".
const PANEL_VERTICAL_METRIC_GROUPS: readonly (readonly [
  characters: string,
  minimum: number,
  maximum: number,
])[] = [
  ["[]{}", 0.38, 1.36],
  ["$", 0.41, 1.28],
  ["|", 0.44, 1.28],
  ["()", 0.44, 1.29],
  ["*", 0.45, 0.8],
  ["i", 0.46, 1.2],
  ["j", 0.46, 1.41],
  ["8", 0.48, 1.21],
  ["2f", 0.49, 1.2],
  ["%&69?CGOS", 0.49, 1.21],
  ["0", 0.49, 1.22],
  ["Q", 0.49, 1.3],
  ["`", 0.5, 0.63],
  ["\"'", 0.5, 0.74],
  ["′", 0.5, 0.75],
  ["^", 0.5, 0.91],
  ["!/147ABDEFHIKLMNPRTVWXYZ\\hkl", 0.5, 1.2],
  ["35JUbd", 0.5, 1.21],
  ["#", 0.52, 1.17],
  ["@", 0.52, 1.27],
  ["t", 0.55, 1.2],
  ["<>", 0.66, 1.16],
  ["mnr", 0.67, 1.2],
  ["aceos", 0.67, 1.21],
  ["pq", 0.67, 1.39],
  ["g", 0.67, 1.41],
  ["wvxz", 0.68, 1.2],
  ["u", 0.68, 1.21],
  ["y", 0.68, 1.41],
  ["+", 0.69, 1.09],
  [":", 0.69, 1.2],
  [";", 0.7, 1.31],
  ["=", 0.74, 1.05],
  ["‹›", 0.74, 1.09],
  ["~", 0.8, 1.02],
  ["−", 0.83, 0.96],
  ["-", 0.84, 0.99],
  [".", 1.02, 1.21],
  [",", 1.04, 1.31],
  ["_", 1.06, 1.2],
];

const PANEL_VERTICAL_METRICS: ReadonlyMap<string, VerticalGlyphMetric> = new Map(
  PANEL_VERTICAL_METRIC_GROUPS.flatMap(([characters, minimum, maximum]) =>
    [...characters].map((character) => [character, [minimum, maximum] as const] as const),
  ),
);

const PANEL_BITMAP_UNSUPPORTED = /[∿⌁⊓△→]/u;
const PANEL_BITMAP_BASELINE_OFFSET = 19 / 96;

function primaryFontFamily(fontFamily: string) {
  return fontFamily
    .split(",", 1)[0]!
    .trim()
    .replace(/^['"]|['"]$/g, "")
    .toLowerCase();
}

function bitmapVerticalBounds(
  text: GraphicText,
  lines: readonly string[],
  lineHeight: number,
): readonly [minimum: number, maximum: number] | undefined {
  if (
    text.stroke ||
    primaryFontFamily(text.fontFamily) !== "patchwork panel" ||
    PANEL_BITMAP_UNSUPPORTED.test(text.value)
  ) {
    return undefined;
  }

  let minimum = Number.POSITIVE_INFINITY;
  let maximum = Number.NEGATIVE_INFINITY;
  for (const [lineIndex, line] of lines.entries()) {
    for (const character of line) {
      if (/\s/u.test(character)) continue;
      const metric = PANEL_VERTICAL_METRICS.get(character);
      if (!metric) return undefined;
      const lineOffset = lineIndex * lineHeight;
      minimum = Math.min(minimum, lineOffset + metric[0] * text.fontSize);
      maximum = Math.max(maximum, lineOffset + metric[1] * text.fontSize);
    }
  }
  if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) return undefined;

  const lineBoxHeight = Math.max(text.fontSize, lines.length * lineHeight);
  const verticalAnchor =
    text.verticalAlign === "middle" ? 0.5 : text.verticalAlign === "bottom" ? 1 : 0;
  const anchorShift =
    verticalAnchor * (lineBoxHeight + PANEL_BITMAP_BASELINE_OFFSET * text.fontSize);
  return [minimum - anchorShift, maximum - anchorShift];
}

function lineAdvance(line: string, fontSize: number, letterSpacing: number) {
  const characters = [...line];
  const glyphAdvance = characters.reduce(
    (width, character) => width + (PANEL_ADVANCES[character] ?? 0.46),
    0,
  );
  return glyphAdvance * fontSize + Math.max(0, characters.length - 1) * letterSpacing;
}

/**
 * Returns deterministic local bounds for the rendered glyph run. These use
 * the bundled panel font's advance metrics, while remaining a safe estimate
 * for the alternate condensed fonts offered by the inspector.
 */
export function textGraphicContentBounds(text: GraphicText): Rect {
  const lines = text.value.split("\n");
  const horizontalScale = text.horizontalScale ?? 1;
  const width =
    Math.max(0, ...lines.map((line) => lineAdvance(line, text.fontSize, text.letterSpacing ?? 0))) *
    horizontalScale;
  const lineHeight = text.lineHeight ?? text.fontSize * 1.08;
  const lineBoxHeight = Math.max(text.fontSize, lines.length * lineHeight);
  const horizontalAnchor = text.align === "center" ? 0.5 : text.align === "right" ? 1 : 0;
  const verticalAnchor =
    text.verticalAlign === "middle" ? 0.5 : text.verticalAlign === "bottom" ? 1 : 0;
  const bitmapBounds = bitmapVerticalBounds(text, lines, lineHeight);
  const verticalMinimum = bitmapBounds?.[0] ?? -lineBoxHeight * verticalAnchor;
  const verticalMaximum = bitmapBounds?.[1] ?? lineBoxHeight * (1 - verticalAnchor);
  const strokePadding = (text.stroke?.width ?? 0) / 2;

  return {
    x: text.x - width * horizontalAnchor - strokePadding,
    y: text.y + verticalMinimum - strokePadding,
    width: width + strokePadding * 2,
    height: verticalMaximum - verticalMinimum + strokePadding * 2,
  };
}
